import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DiscussionsApi } from "../api/Endpoints/Discussions";

const POLL_MS = 30000;
const data = (res) => res.data?.data;
// A missing Works account (401) or a hidden thread (404) won't fix itself on retry.
const retry = (count, err) => ![400, 401, 403, 404].includes(err?.status) && count < 2;

// The caller as the Work API knows them (Works user id differs from the Accounts id).
export function useDiscussionMe() {
  return useQuery({
    queryKey: ["discussions", "me"],
    queryFn: () => DiscussionsApi.me().then(data),
    staleTime: Infinity,
    retry,
  });
}

export function useDiscussions(params) {
  return useQuery({
    queryKey: ["discussions", params],
    // The API lifts pagination meta to the top of the envelope: { data: rows, meta }.
    queryFn: () => DiscussionsApi.list(params).then((res) => ({ data: res.data?.data ?? [], meta: res.data?.meta })),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
    retry,
  });
}

export function useDiscussionUnreadCount() {
  return useQuery({
    queryKey: ["discussions", "unread-count"],
    queryFn: () => DiscussionsApi.unreadCount().then((res) => data(res)?.count ?? 0),
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useDiscussionCounts() {
  return useQuery({
    queryKey: ["discussions", "counts"],
    queryFn: () => DiscussionsApi.counts().then(data),
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    retry,
  });
}

export function useDiscussionEdits(id, responseId, enabled) {
  return useQuery({
    queryKey: ["discussion-edits", id, responseId],
    queryFn: () => DiscussionsApi.edits(id, responseId).then(data),
    enabled,
    retry,
  });
}

export function useDiscussion(id) {
  return useQuery({
    queryKey: ["discussion", id],
    queryFn: () => DiscussionsApi.get(id).then(data),
    enabled: !!id,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    retry,
  });
}

export function useDiscussionPeople(search) {
  return useQuery({
    queryKey: ["discussion-people", search],
    queryFn: () => DiscussionsApi.people(search).then(data),
    staleTime: 5 * 60000,
    keepPreviousData: true,
    retry,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return (id) => {
    qc.invalidateQueries({ queryKey: ["discussions"] });
    if (id) {
      qc.invalidateQueries({ queryKey: ["discussion", id] });
      qc.invalidateQueries({ queryKey: ["discussion-edits", id] });
    }
  };
}

export function useCreateDiscussion() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body) => DiscussionsApi.create(body).then(data),
    onSuccess: () => invalidate(),
  });
}

export function useDiscussionActions(id) {
  const invalidate = useInvalidate();
  const done = { onSuccess: () => invalidate(id) };
  return {
    respond: useMutation({ mutationFn: (text) => DiscussionsApi.respond(id, text), ...done }),
    setStatus: useMutation({ mutationFn: (s) => DiscussionsApi.setStatus(id, s), ...done }),
    addPeople: useMutation({ mutationFn: (ids) => DiscussionsApi.addPeople(id, ids), ...done }),
    removePerson: useMutation({ mutationFn: (uid) => DiscussionsApi.removePerson(id, uid), ...done }),
    markRead: useMutation({ mutationFn: () => DiscussionsApi.markRead(id), onSuccess: () => invalidate() }),
    // responseId null edits the original message.
    edit: useMutation({
      mutationFn: ({ responseId, text, title }) =>
        responseId ? DiscussionsApi.editResponse(id, responseId, text) : DiscussionsApi.editPost(id, { title, description: text }),
      ...done,
    }),
  };
}
